import React, { createContext, useContext, useState, useRef, useCallback } from 'react';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Auth Context - Single Source of Truth for Admin Authentication
 * 
 * Implements single-flight health checks to prevent redirect loops
 * State machine: gateOk → sessionOk → admin access
 */
export function AuthProvider({ children }) {
  const [state, setState] = useState({
    loading: false,
    gateOk: null,      // null = unknown, true = has gate cookie, false = no gate cookie
    sessionOk: null    // null = unknown, true = authenticated, false = not authenticated
  });

  // Single-flight locks prevent duplicate health checks
  const gateCheckedRef = useRef(false);
  const sessionCheckedRef = useRef(false);
  const redirecting = useRef(false);

  /**
   * Verify if gate cookie is present
   * Only runs once per mount
   */
  const verifyGateOnce = useCallback(async () => {
    if (gateCheckedRef.current) return;
    gateCheckedRef.current = true;

    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/auth/gate-status`, {
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        setState(s => ({ ...s, gateOk: data.gateOk }));
      } else {
        setState(s => ({ ...s, gateOk: false }));
      }
    } catch (error) {
      console.error('Gate check error:', error);
      setState(s => ({ ...s, gateOk: null })); // Unknown is fine
    }
  }, []);

  /**
   * Verify if admin session is valid
   * Only runs once per mount
   */
  const verifySessionOnce = useCallback(async () => {
    if (sessionCheckedRef.current) return;
    sessionCheckedRef.current = true;

    setState(s => ({ ...s, loading: true }));

    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/auth/health`, {
        credentials: 'include'
      });
      
      setState(s => ({ 
        ...s, 
        sessionOk: res.ok, 
        loading: false 
      }));
    } catch (error) {
      console.error('Session check error:', error);
      setState(s => ({ 
        ...s, 
        sessionOk: false, 
        loading: false 
      }));
    }
  }, []);

  /**
   * Sign out user and clear state
   */
  const signOut = useCallback(async () => {
    try {
      await fetch(`${BACKEND_URL}/api/admin/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }

    // Reset all state
    setState({
      loading: false,
      gateOk: null,
      sessionOk: false
    });

    // Reset refs so checks can run again
    gateCheckedRef.current = false;
    sessionCheckedRef.current = false;
    redirecting.current = false;
  }, []);

  /**
   * Reset checks (useful after successful login)
   */
  const resetChecks = useCallback(() => {
    gateCheckedRef.current = false;
    sessionCheckedRef.current = false;
    redirecting.current = false;
  }, []);

  /**
   * Mark session as valid (after successful login)
   */
  const setSessionValid = useCallback(() => {
    setState(s => ({ ...s, sessionOk: true, loading: false }));
    sessionCheckedRef.current = true;
  }, []);

  const value = {
    state,
    verifyGateOnce,
    verifySessionOnce,
    signOut,
    resetChecks,
    setSessionValid,
    redirecting
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to access auth context
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
